import React from 'react';
import { LegalLayout } from '@/components/layouts/LegalLayout';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, Shield, CheckCircle, FileText } from 'lucide-react';

export const ConformiteLogicielle: React.FC = () => {
  const handleDownloadAttestation = () => {
    // Ici vous implémenterez la génération/téléchargement du PDF
    // PDF download implementation needed
    // Exemple de génération d'un lien de téléchargement
    // window.open('/attestations/conformite_[SIRET]_2024.pdf', '_blank');
  };

  return (
    <LegalLayout>
      <div className="prose prose-slate dark:prose-invert max-w-none">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-3xl font-bold">Attestation de conformité logicielle</h1>
          <Badge variant="secondary" className="text-xs">Art. 286 CGI</Badge>
        </div>
        
        <Alert className="mb-8">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            Cette attestation certifie que notre logiciel de caisse respecte la réglementation française 
            anti-fraude à la TVA en vigueur depuis le 1er janvier 2018.
          </AlertDescription>
        </Alert>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Cadre réglementaire</h2>
          
          <div className="bg-muted/30 p-4 rounded-lg mb-4">
            <h3 className="font-semibold mb-2">📋 Références légales</h3>
            <ul className="text-sm space-y-1">
              <li>• <strong>Article 286 du Code Général des Impôts</strong></li>
              <li>• <strong>Arrêté du 3 décembre 2018</strong> (NF 525)</li>
              <li>• <strong>BOI-TVA-DECLA-30-10-30</strong> (Bulletin Officiel des Impôts)</li>
              <li>• <strong>Loi de finances 2016</strong> (Loi n° 2015-1785)</li>
            </ul>
          </div>

          <div className="grid gap-4">
            <div className="border border-green-200 bg-green-50 dark:bg-green-950/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Obligations respectées
              </h3>
              <ul className="text-sm space-y-1">
                <li>✅ Inaltérabilité des données de vente</li>
                <li>✅ Sécurisation des données</li>
                <li>✅ Conservation et archivage</li>
                <li>✅ Traçabilité des opérations</li>
              </ul>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Caractéristiques techniques certifiées</h2>
          
          <div className="space-y-6">
            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">🔒 Inaltérabilité des données</h3>
              <div className="bg-primary/5 p-3 rounded">
                <ul className="text-sm space-y-1">
                  <li>• <strong>Chiffrement des données :</strong> AES-256</li>
                  <li>• <strong>Intégrité :</strong> Fonction de hachage cryptographique</li>
                  <li>• <strong>Horodatage :</strong> Serveur de temps certifié</li>
                  <li>• <strong>Séquencement :</strong> Numérotation continue et chronologique</li>
                </ul>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">🛡️ Sécurisation</h3>
              <div className="bg-primary/5 p-3 rounded">
                <ul className="text-sm space-y-1">
                  <li>• <strong>Authentification :</strong> Multi-facteurs obligatoire</li>
                  <li>• <strong>Journalisation :</strong> Tous les événements tracés</li>
                  <li>• <strong>Contrôle d'accès :</strong> Gestion des droits utilisateur</li>
                  <li>• <strong>Audit trail :</strong> Piste d'audit complète</li>
                </ul>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="font-semibold mb-3">📦 Conservation des données</h3>
              <div className="bg-primary/5 p-3 rounded">
                <ul className="text-sm space-y-1">
                  <li>• <strong>Durée minimum :</strong> 6 ans à partir de la dernière opération</li>
                  <li>• <strong>Format :</strong> Données structurées et exploitables</li>
                  <li>• <strong>Accessibilité :</strong> Consultation immédiate sur demande</li>
                  <li>• <strong>Sauvegarde :</strong> Multiple et géographiquement distribuée</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Attestation officielle</h2>
          
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 border border-primary/20 p-6 rounded-lg">
            <div className="text-center mb-4">
              <FileText className="w-12 h-12 mx-auto text-primary mb-2" />
              <h3 className="font-bold text-lg">Certificat de conformité NF 525</h3>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <strong>Éditeur :</strong> [Nom de votre société - À remplir]
                </div>
                <div>
                  <strong>Version logicielle :</strong> v2024.1
                </div>
                <div>
                  <strong>Date de certification :</strong> [Date - À remplir]
                </div>
                <div>
                  <strong>Numéro de certificat :</strong> [Numéro - À remplir]
                </div>
                <div>
                  <strong>Organisme certificateur :</strong> [Nom - À remplir]
                </div>
                <div>
                  <strong>Validité :</strong> [Date fin - À remplir]
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Button 
                onClick={handleDownloadAttestation}
                className="bg-primary hover:bg-primary/90"
                size="lg"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger l'attestation PDF
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Document officiel à présenter lors d'un contrôle fiscal
              </p>
            </div>
          </div>
        </section>

        <Separator className="my-6" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Obligations du commerçant</h2>
          
          <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 p-4 rounded-lg">
            <h3 className="font-semibold mb-3">⚠️ À retenir</h3>
            <ul className="text-sm space-y-2">
              <li>
                <strong>• Utilisation obligatoire :</strong> Tout logiciel de caisse doit être certifié 
                conforme à la norme NF 525
              </li>
              <li>
                <strong>• Conservation :</strong> Cette attestation doit être conservée et présentée 
                lors de tout contrôle fiscal
              </li>
              <li>
                <strong>• Mise à jour :</strong> Vérifiez régulièrement que votre logiciel 
                reste conforme aux évolutions réglementaires
              </li>
              <li>
                <strong>• Sanctions :</strong> L'utilisation d'un logiciel non conforme 
                expose à une amende de 7 500€
              </li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4">Support technique</h2>
          
          <div className="grid gap-4">
            <div className="bg-primary/10 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">📞 Assistance conformité</h3>
              <p className="text-sm mb-2">
                Notre équipe technique vous accompagne pour toute question relative à la conformité :
              </p>
              <p className="text-sm">
                <strong>Email :</strong> [conformite@votredomaine.com - À remplir]<br/>
                <strong>Téléphone :</strong> [Numéro - À remplir]<br/>
                <strong>Horaires :</strong> Lundi-Vendredi, 9h-17h
              </p>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">📋 Documentation technique</h3>
              <ul className="text-sm space-y-1">
                <li>• Guide d'utilisation conforme NF 525</li>
                <li>• Procédures de sauvegarde et restauration</li>
                <li>• Manuel de contrôle fiscal</li>
                <li>• Historique des mises à jour de conformité</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </LegalLayout>
  );
};